import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import {
  Alert,
  Box,
  Checkbox,
  Paper,
  IconButton,
  List,
  ListItem,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogTitle from "@mui/material/DialogTitle";
import React, { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import {
  fetchChecklist,
  sendMailRequest,
  updateChecklistTask,
} from "../api/checklist";

import FeedbackSnackbar from "../components/FeedbackSnackbar";
import Loading from "../components/Loading";
import PageHeader from "../components/layout/PageHeader";
import { TaskBody } from "../components/TaskBase";
import { CHECKLIST_CONFIG } from "../config";

// エラーダイアログコンポーネント
function ErrorDialog({ open, onClose, errorMessage }) {
  return (
    <Dialog
      open={open}
      onClose={onClose}
      aria-labelledby="error-dialog-title"
      aria-describedby="error-dialog-description"
    >
      <DialogTitle id="error-dialog-title">
        {"通信に失敗しました。もう１度試してください。"}
      </DialogTitle>
      <DialogContent>
        <DialogContentText id="error-dialog-description">
          {errorMessage}
        </DialogContentText>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="primary" autoFocus>
          OK
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// 新しい TaskRow コンポーネント
function TaskRow({
  isHeader = false,
  task,
  crewChecked,
  supervisorChecked,
  onCrewChange,
  onSupervisorChange,
  formatDateFn,
  drawTopBorder,
  isEditingMemo,
  memoText,
  onMemoChange,
  onEditMemoClick,
  onSaveMemo,
  onCancelMemo,
}) {
  const { crew, supervisor, task: taskConfig } = CHECKLIST_CONFIG.columns;

  const gridTemplateColumns = `${crew.width}px ${supervisor.width}px 1fr`;

  return (
    <ListItem
      dense
      style={{
        borderTop: drawTopBorder ? "1px solid #ccc" : "none",
        display: "grid",
        gridTemplateColumns: gridTemplateColumns,
        alignItems: "start",
        padding: "8px",
        minHeight: "48px",
      }}
    >
      {isHeader ? (
        <>
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              height: 32,
            }}
          >
            <Typography variant="body2" color="text.secondary">
              {crew.title}
            </Typography>
          </Box>
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              height: 32,
            }}
          >
            <Typography variant="body2" color="text.secondary">
              {supervisor.title}
            </Typography>
          </Box>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              height: 32,
              ml: 1,
            }}
          >
            <Typography variant="body2" color="text.secondary">
              {taskConfig.title}
            </Typography>
          </Box>
        </>
      ) : (
        <>
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "flex-start",
              width: "100%",
              minHeight: "100%",
            }}
          >
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                width: "100%",
                pt: "2px",
              }}
            >
              <Checkbox
                checked={crewChecked}
                tabIndex={-1}
                onChange={onCrewChange}
                size="small"
              />
            </Box>
            {task.crew_completed_at && (
              <Typography
                variant="caption"
                sx={{
                  fontSize: "0.7rem",
                  width: "100%",
                  textAlign: "center",
                }}
              >
                {formatDateFn(task.crew_completed_at)}
              </Typography>
            )}
          </Box>

          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "flex-start",
              width: "100%",
              minHeight: "100%",
            }}
          >
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                width: "100%",
                pt: "2px",
              }}
            >
              <Checkbox
                checked={supervisorChecked}
                tabIndex={-1}
                onChange={onSupervisorChange}
                size="small"
              />
            </Box>
            {task.supervisor_completed_at && (
              <Typography
                variant="caption"
                sx={{
                  fontSize: "0.7rem",
                  width: "100%",
                  textAlign: "center",
                }}
              >
                {formatDateFn(task.supervisor_completed_at)}
              </Typography>
            )}
          </Box>

          <Box
            sx={{
              ml: 1,
              overflow: "hidden",
              minWidth: 0,
              display: "flex",
              alignItems: "flex-start",
              width: "100%",
            }}
          >
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                flexGrow: 1,
                minWidth: 0,
              }}
            >
              <TaskBody
                name={task.name}
                detail={task.detail}
                images={task.images}
                recurrenceType={task.recurrence_type}
                weeklyDays={task.weekly_days}
                monthlyDays={task.monthly_days}
                compact={true}
              />
              {isEditingMemo ? (
                <>
                  <TextField
                    value={memoText}
                    onChange={(e) => onMemoChange(e.target.value)}
                    placeholder="メモを記入することができます。"
                    multiline
                    minRows={2}
                    fullWidth
                    size="small"
                    autoFocus
                    sx={{ mt: 0.5, width: "100%" }}
                    onKeyDown={(e) => {
                      if (e.key === "Escape") onCancelMemo();
                    }}
                  />
                  <Box sx={{ mt: 0.25, display: "flex", alignItems: "center", gap: 0.25 }}>
                    <IconButton size="small" onClick={onSaveMemo} sx={{ p: 0.125 }}>
                      <CheckIcon fontSize="small" />
                    </IconButton>
                    <IconButton size="small" onClick={onCancelMemo} sx={{ p: 0.125 }}>
                      <CloseIcon fontSize="small" />
                    </IconButton>
                  </Box>
                </>
              ) : memoText ? (
                <Typography
                  variant="body2"
                  color="text.secondary"
                  onClick={onEditMemoClick}
                  sx={{
                    whiteSpace: "pre-wrap",
                    mt: 0.5,
                    cursor: "pointer",
                    borderRadius: 0.5,
                    px: 0.5,
                    mx: -0.5,
                    "&:hover": { backgroundColor: "action.hover" },
                  }}
                >
                  {memoText}
                </Typography>
              ) : (
                <Typography
                  variant="caption"
                  color="text.disabled"
                  onClick={onEditMemoClick}
                  sx={{
                    mt: 0.5,
                    cursor: "pointer",
                    "&:hover": { color: "primary.main" },
                  }}
                >
                  + メモ
                </Typography>
              )}
            </Box>
          </Box>

        </>
      )}
    </ListItem>
  );
}

function Task({ task, checklistPublicId, setChecklist, drawTopBorder, onError }) {
  const [crewChecked, setCrewChecked] = useState(task.crew_status !== 0);
  const [supervisorChecked, setSupervisorChecked] = useState(
    task.supervisor_status !== 0
  );
  const [isEditingMemo, setIsEditingMemo] = useState(false);
  const [memoText, setMemoText] = useState(task.memo || "");
  const updateQueue = useRef([]);

  const processUpdateQueue = async () => {
    if (updateQueue.current.length === 0) return;

    const update = updateQueue.current[0];
    try {
      const response = await updateChecklistTask(
        checklistPublicId,
        task.public_id,
        update
      );

      if (!response.ok) {
        throw new Error(`サーバーエラー: ${response.status}`);
      }

      const data = await response.json();

      setChecklist((prevChecklist) => {
        const updatedChecklist = JSON.parse(JSON.stringify(prevChecklist));
        for (let taskGroup of updatedChecklist.task_groups) {
          for (let t of taskGroup.tasks) {
            if (t.id === task.id) {
              t.crew_status = data.crew_status;
              t.supervisor_status = data.supervisor_status;
              t.crew_completed_at = data.crew_completed_at;
              t.supervisor_completed_at = data.supervisor_completed_at;
              t.memo = data.memo;
              break;
            }
          }
        }
        return updatedChecklist;
      });

      updateQueue.current.shift();
      if (updateQueue.current.length > 0) {
        processUpdateQueue();
      }
    } catch (error) {
      console.error("Failed to update task:", error);
      const msg =
        error.message === "Failed to fetch"
          ? "APIエンドポイントが見つかりません。バックエンドの実装を確認してください。"
          : `タスクの更新に失敗しました: ${error.message}`;
      onError(msg);
      setCrewChecked(task.crew_status !== 0);
      setSupervisorChecked(task.supervisor_status !== 0);
      setMemoText(task.memo || "");
      updateQueue.current = [];
    }
  };

  const queueUpdate = (update) => {
    updateQueue.current.push(update);
    if (updateQueue.current.length === 1) {
      processUpdateQueue();
    }
  };

  const handleCrewCheckboxChange = () => {
    const newCheckedState = !crewChecked;
    setCrewChecked(newCheckedState);

    queueUpdate({
      crew_status: newCheckedState ? 1 : 0,
      crew_completed_at: newCheckedState ? new Date().toISOString() : null,
    });
  };

  const handleSupervisorCheckboxChange = () => {
    const newCheckedState = !supervisorChecked;
    setSupervisorChecked(newCheckedState);

    queueUpdate({
      supervisor_status: newCheckedState ? 1 : 0,
      supervisor_completed_at: newCheckedState
        ? new Date().toISOString()
        : null,
    });
  };

  const handleEditMemoClick = () => {
    setIsEditingMemo(true);
    setMemoText(task.memo || "");
  };

  const handleSaveMemo = () => {
    setIsEditingMemo(false);
    if (memoText !== task.memo) {
      queueUpdate({ memo: memoText });
    }
  };

  const handleCancelMemo = () => {
    setIsEditingMemo(false);
    setMemoText(task.memo || "");
  };

  const formatDate = (dateString) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    const month = date.getMonth() + 1;
    const day = date.getDate();
    const hours = String(date.getHours()).padStart(2, "0");
    const minutes = String(date.getMinutes()).padStart(2, "0");
    return `${month}/${day} ${hours}:${minutes}`;
  };

  return (
    <TaskRow
      isHeader={false}
      task={task}
      crewChecked={crewChecked}
      supervisorChecked={supervisorChecked}
      onCrewChange={handleCrewCheckboxChange}
      onSupervisorChange={handleSupervisorCheckboxChange}
      formatDateFn={formatDate}
      drawTopBorder={drawTopBorder}
      isEditingMemo={isEditingMemo}
      memoText={memoText}
      onMemoChange={setMemoText}
      onEditMemoClick={handleEditMemoClick}
      onSaveMemo={handleSaveMemo}
      onCancelMemo={handleCancelMemo}
    />
  );
}

// タスクグループのコンポーネント
function TaskGroup({ checklistPublicId, group, setChecklist, onError }) {
  const sortedTasks = [...group.tasks].sort((a, b) => a.order - b.order);

  return (
    <Paper key={group.id} sx={{ p: 1.5, borderRadius: 2.5 }}>
      <div
        style={{
          backgroundColor: "#f5f5f5",
          padding: "6px 12px",
          borderRadius: "10px",
        }}
      >
        <Typography variant="subtitle2" style={{ fontWeight: 700 }}>
          {group.name}
        </Typography>
      </div>
      <List>
        <TaskRow isHeader={true} drawTopBorder={false} />

        {sortedTasks.map((task) => (
          <Task
            key={task.id}
            task={task}
            checklistPublicId={checklistPublicId}
            setChecklist={setChecklist}
            drawTopBorder={false}
            onError={onError}
          />
        ))}
      </List>
    </Paper>
  );
}

// メインコンポーネント
function UseCheckList() {
  const { checklistId: checklistPublicId } = useParams();
  const [checklist, setChecklist] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showError, setShowError] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [feedback, setFeedback] = useState({
    open: false,
    message: "",
    severity: "info",
  });

  const handleSendMail = async () => {
    try {
      await sendMailRequest(checklist.name, checklist.assignee);
      setFeedback({
        open: true,
        message: "メール送信リクエストを送信しました。",
        severity: "success",
      });
    } catch (error) {
      console.error("Error sending mail request:", error);
      setErrorMessage("メール送信リクエストに失敗しました。" + error.message);
      setShowError(true);
    }
  };

  useEffect(() => {
    async function fetchData() {
      try {
        const checklistData = await fetchChecklist(checklistPublicId);
        setChecklist(checklistData);
        setLoading(false);
      } catch (error) {
        console.error("Error fetching checklist:", error);
        setLoading(false);
      }
    }

    fetchData();
  }, [checklistPublicId]);

  if (loading) {
    return <Loading />;
  }

  if (!checklist) {
    return <Alert severity="warning">チェックリストが見つかりませんでした。</Alert>;
  }

  return (
    <Stack spacing={2}>
      <PageHeader
        title={checklist.name}
        action={
          <Button variant="contained" onClick={handleSendMail}>
            メールを送信
          </Button>
        }
      />

      <Box sx={{ display: "grid", gap: 1.5 }}>
        {[...checklist.task_groups]
          .sort((a, b) => a.order - b.order)
          .map((group) => (
            <TaskGroup
              key={group.id}
              checklistPublicId={checklist.public_id}
              group={group}
              setChecklist={setChecklist}
              onError={(msg) => {
                setErrorMessage(msg);
                setShowError(true);
              }}
            />
          ))}
      </Box>

      <ErrorDialog
        open={showError}
        onClose={() => {
          setShowError(false);
          setErrorMessage("");
        }}
        errorMessage={errorMessage}
      />
      <FeedbackSnackbar
        open={feedback.open}
        message={feedback.message}
        severity={feedback.severity}
        onClose={() => setFeedback((prev) => ({ ...prev, open: false }))}
      />
    </Stack>
  );
}

export default UseCheckList;
